import { EMPTY } from 'rxjs';
import { ActivatedRoute, Params } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import {
  ActionConfigDTO,
  FieldConfigDTO,
  FormRowConfigDTO,
  MaintainTabDTO,
  MenuDTO,
} from '../../../../system/system-dtos';
import { PageRequestDTO, PageResponseDTO } from '../../../../common/paging-dto';
import { EncodingUtils } from '../../../../common/encoding-utils';
import { MaintainService } from '../maintain.service';
import { ExtractedActions, SimpleTableActionDTO } from '../../../../common/event-dtos';

export abstract class DataSource {
  public static getObjectIfJsonOrString(str: string): any {
    try {
      return JSON.parse(str);
    } catch (exception) {
      return str;
    }
  }

  public static getObjectIfJsonOrNull(str: string): any {
    try {
      return JSON.parse(str);
    } catch (exception) {
      return null;
    }
  }

  abstract doGetData(
    menuTab: MenuDTO,
    currentTab: MaintainTabDTO,
    pageRequest: PageRequestDTO,
    filters: { [p: string]: FieldConfigDTO },
    id: any,
    context: any,
    queryParams: Params
  ): Observable<PageResponseDTO<any>>;

  abstract getTabs(menuConfig: MenuDTO): MaintainTabDTO[];

  abstract getRows(menuConfig: MenuDTO): FormRowConfigDTO[];

  abstract getTitle(menuConfig: MenuDTO): string;

  abstract extractActions(menuConfig: MenuDTO, tabConfig: MaintainTabDTO, element: any): ExtractedActions;

  abstract getTabsWithCount(menuConfig: MenuDTO): Observable<{ [p: string]: number }>;

  abstract getOverallRestActions(menu: MenuDTO): ActionConfigDTO[];

  dataChanged(
    menuConfig: MenuDTO,
    currentTab: MaintainTabDTO,
    pageRequest: PageRequestDTO,
    filters: { [p: string]: FieldConfigDTO },
    id: any,
    context: any = {},
    queryParams: Params = {}
  ): Observable<PageResponseDTO<any>> {
    return EMPTY;
  }

  getPageMode(menuConfig: MenuDTO): PAGINATION_MODE {
    return PAGINATION_MODE.SERVER_SIDE;
  }

  extractRowData(element: any, menu: MenuDTO) {
    return element;
  }

  isInMemoryFilter(menu: MenuDTO): boolean {
    return false;
  }

  getNoteForData(menu: MenuDTO): string {
    return null;
  }
}

export abstract class SpecificEndpointDataSource extends DataSource {
  public constructor(
    private route: ActivatedRoute,
    private maintainService: MaintainService
  ) {
    super();
  }

  public static parseTag(tag: string): string {
    return tag.charAt(0).toUpperCase() + tag.slice(1).replace('-', ' ');
  }

  getTitle(menuConfig: MenuDTO): string {
    return menuConfig.label;
  }

  public doGetData(
    menuConfig: MenuDTO,
    currentTab: MaintainTabDTO,
    pageRequest: PageRequestDTO,
    filters: { [p: string]: FieldConfigDTO },
    id: any,
    context: any = {},
    queryParams: Params = {}
  ): Observable<PageResponseDTO<any>> {
    let { selectAllUrl } = currentTab.endpoints;

    const fields = this.maintainService.getFields(selectAllUrl);
    if (Object.keys(fields).length > 0) {
      selectAllUrl = selectAllUrl.replace(fields[Object.keys(fields)[0]], id);
    }

    return this.maintainService.selectAllData(selectAllUrl, pageRequest, this.convertFiltersToHttpParams(filters));
  }

  private convertFiltersToHttpParams(filters: { [p: string]: FieldConfigDTO }): HttpParams {
    let httpParams = new HttpParams();

    Object.keys(filters || {})
      .filter((k) => !!filters[k].value)
      .forEach((k) => {
        const filterValue = filters[k].value;
        if (!Array.isArray(filterValue)) {
          httpParams = httpParams.set(EncodingUtils.encodeRequest(k), filterValue);
        } else {
          filterValue.forEach(
            (value) => (httpParams = httpParams.append(k, DataSource.getObjectIfJsonOrString(value)))
          );
        }
      });

    return httpParams;
  }

  extractActions(menuConfig: MenuDTO, tab: MaintainTabDTO, element: any): ExtractedActions {
    const simple: SimpleTableActionDTO[] = tab.tableView.actions.map((a) => ({
      label: SpecificEndpointDataSource.parseTag(a.tag),
      command: a.tag,
    }));
    return {
      simple,
    };
  }

  getTabsWithCount(menuConfig: MenuDTO): Observable<{ [p: string]: number }> {
    return new Observable<{ [p: string]: number }>((observer) => {
      observer.next(null);
      observer.complete();
    });
  }
}

export enum PAGINATION_MODE {
  SERVER_SIDE = 'SERVER_SIDE',
  IN_MEMORY = 'IN_MEMORY',
  NONE = 'NONE', // might not work properly
}
